import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { Box, Dialog, DialogContent, Typography, IconButton, Grid, InputAdornment, Popover, Chip, FormHelperText } from '@material-ui/core'
import Button from 'UI/Button/ButtonWithPreloader'
import { ReactComponent as SplitIcon } from 'assets/svg/SplitIcon.svg'
import { ReactComponent as CloseIcon } from 'assets/svg/CloseIcon.svg'
import { useQuery } from 'react-query'
import { getIsoLight } from 'services/iso'
import { IIsoListItemLight } from 'typescript/interfaces/iso'
import ISOAutoComplete from 'components/ISOAutoComplete'
import DecimalTextField from 'UI/TextField/DecimalTextField'
import SplitIsoCommissionForm from 'components/InhouseDealForm/SplitIsoCommissionForm'
import { useSnackbar } from 'notistack'
import { 
  COMISSION_NEED_TO_BE_LOWER_THAN_15_PERCENT, 
  COMMISSION_DUE_DATE_IN_PAST, 
  COMMISSION_DATE_EARLIER, 
  INVALID_DATE_MESSAGE } from 'constants/errors'
import { COMMISSION_WAS_PAID_WARNING } from 'constants/warnings'
import { bankRounding, calcProportionInPercents } from 'utils/rounding'
import { DECIMAL_PLACES } from 'constants/calcConstants'
import DatePicker from 'UI/DatePicker/DatePicker'
import { isDateInPast, isSameCalendarDate, isBeforeDate } from 'utils/dateUtils'
import { isValid } from 'date-fns'
import convertDateForPicker from 'utils/convertDateForPicker'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'
import { useISORepSelectorStyles } from './styles'

interface ISOSelectorProps {
  value?: {
    ids?: number[]
    fee?: number
    splitCommission?: any[]
    commissionDueDate?: string
  }
  onChange: (value: any) => void
  disabled?: boolean
  advanceAmount?: number
  dealStartDate?: string
  hideCommissionDueDateField: boolean
  disableCommissionDueDateField: boolean
}

const ISOSelector: React.FC<ISOSelectorProps> = (
  { value, onChange, disabled = false, advanceAmount = 0, dealStartDate, hideCommissionDueDateField, disableCommissionDueDateField }) => {
  const classes = useISORepSelectorStyles()
  const { enqueueSnackbar } = useSnackbar()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [splitPopoverAnchor, setSplitPopoverAnchor] = useState<HTMLElement | null>(null)
  const [dateError, setDateError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    ids: value?.ids || [],
    fee: value?.fee?.toString() || '',
    splitCommission: value?.splitCommission || [],
    commissionDueDate: value?.commissionDueDate || undefined
  })

  // Fetch data for autocomplete
  const { data: isoData } = useQuery(['iso'], () => getIsoLight({ PageSize: 4000 }))

  const selectedItems = useMemo(() => {
    if (isoData) {
      return isoData.data.filter((item: IIsoListItemLight) => formData.ids.includes(item.id))
    }
    return []
  }, [formData.ids, isoData])

  // Update formData when value prop changes (e.g., when deal data is loaded)
  // Only reset form data if dialog is not open to prevent losing user progress
  useEffect(() => {
    if (!isDialogOpen) {
      setFormData({
        ids: value?.ids || [],
        fee: value?.fee?.toString() || '',
        splitCommission: value?.splitCommission || [],
        commissionDueDate: value?.commissionDueDate
      })
    }
  }, [value, isDialogOpen])

  const handleOpenDialog = () => {
    if (value) {
      setFormData({
        ids: value.ids || [],
        fee: value.fee?.toString() || '',
        splitCommission: value.splitCommission || [],
        commissionDueDate: value?.commissionDueDate
      })
      setDateError(null);
    }
    setIsDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
  }

  const handleSave = () => {
    const feeAsNumber = parseFloat(formData.fee) || 0

    // Allow 0 fee when no ISOs are selected or when user explicitly sets it to 0
    if (feeAsNumber < 0) {
      enqueueSnackbar(`Please enter a valid ISO fee`, { variant: 'error' })
      return
    }

    // Check if commission exceeds 15% of advance amount
    if (formData.ids.length > 0 && advanceAmount * 0.15 < feeAsNumber) {
      enqueueSnackbar(COMISSION_NEED_TO_BE_LOWER_THAN_15_PERCENT, { variant: 'error' })
      return
    }

    const dataToSave = {
      ...formData,
      fee: feeAsNumber, // Save as number
      commissionDueDate: feeAsNumber ? formData.commissionDueDate : undefined
    }

    onChange(dataToSave)
    setIsDialogOpen(false)
  }

  const handleIdsChange = (newIds: number[]) => {
    setFormData(prev => {
      const prevIds = prev.ids || []
      const prevIdsSorted = [...prevIds].sort()
      const newIdsSorted = [...newIds].sort()
      const isSameSelection =
        prevIdsSorted.length === newIdsSorted.length && prevIdsSorted.every((id, index) => id === newIdsSorted[index])

      return {
        ...prev,
        ids: newIds,
        splitCommission: isSameSelection && newIds.length > 1 ? prev.splitCommission : [],
      }
    })
  }

  const handleFeeChange = (newFee: string) => {
    setFormData(prev => ({
      ...prev,
      fee: newFee,
      splitCommission: [],
    }))
  }

  const handleSplitCommissionChange = (newSplitCommission: any[]) => {
    setFormData(prev => ({
      ...prev,
      splitCommission: newSplitCommission,
    }))
  }

  const renderSplitCommissionForm = () => {
    // Auto-generate equal split if no split commission exists
    const getAutoSplitValues = () => {
      // Check if existing split commission matches current ISO selection
      const selectedIds = new Set(selectedItems.map(item => item.id))
      const existingSplitIds = new Set(formData.splitCommission?.map((item: any) => item.id) || [])
      
      // Only use existing values if:
      // 1. Split commission exists and has entries
      // 2. All split commission IDs match currently selected ISO IDs
      // 3. All currently selected ISO IDs have split commission entries
      const hasExistingSplit = formData.splitCommission?.length > 0
      const allSplitIdsMatchSelection = formData.splitCommission?.every((item: any) => selectedIds.has(item.id)) || false
      const allSelectedIdsHaveSplit = selectedItems.length > 0 && selectedItems.every(iso => existingSplitIds.has(iso.id))
      
      if (hasExistingSplit && allSplitIdsMatchSelection && allSelectedIdsHaveSplit) {
        // Use existing values - convert Decimal to number if needed
        return formData.splitCommission.reduce((acc: any, item: any) => {
          let commissionValue = 0
          if (item.value) {
            if (typeof item.value === 'number') {
              commissionValue = item.value
            } else if (item.value && typeof item.value.toNumber === 'function') {
              // Decimal.js object
              commissionValue = item.value.toNumber()
            } else {
              commissionValue = parseFloat(item.value) || 0
            }
          }
          acc[item.id] = commissionValue
          return acc
        }, {})
      }

      // Auto-split equally among selected ISOs by amount, not percentage
      const autoSplitValues: any = {}
      const totalIsos = selectedItems.length
      const totalFeeAmount = parseFloat(formData.fee) || 0

      if (totalIsos > 0 && totalFeeAmount > 0) {
        const baseAmount = bankRounding(totalFeeAmount / totalIsos, DECIMAL_PLACES)
        let distributedAmount = 0

        selectedItems.forEach((iso, index) => {
          if (index === totalIsos - 1) {
            // Last ISO gets the remainder to ensure total equals fee amount
            autoSplitValues[iso.id] = bankRounding(totalFeeAmount - distributedAmount, DECIMAL_PLACES)
          } else {
            autoSplitValues[iso.id] = baseAmount
            distributedAmount += baseAmount
          }
        })
      }

      return autoSplitValues
    }

    const initialValues = getAutoSplitValues()

    return (
      <SplitIsoCommissionForm
        handleClosePopover={() => setSplitPopoverAnchor(null)}
        propsSetFieldValue={(name: string, fieldValue: any) => {
          if (name === 'isoSplitCommission') {
            handleSplitCommissionChange(fieldValue)
          }
        }}
        isoFeeAmount={parseFloat(formData.fee) || 0}
        initialValues={initialValues}
        selectedIsos={selectedItems.map((item: IIsoListItemLight) => ({
          id: item.id,
          name: item.businessName,
        }))}
        disabled={disabled}
        classes={{ root: classes.popoverTextField }}
        name="isoSplitCommission"
      />
    )
  }

  const isPastDateAllowed = useCallback(
    (date: Date) => isSameCalendarDate(date, value?.commissionDueDate),
    [value?.commissionDueDate]
  )

  const handleCommissionDateChange = useCallback((date: MaterialUiPickersDate) => {
    const converted = convertDateForPicker(date) as Date | null
    let commissionDueDate: string | undefined;
    let error: string | null = null;

    if (converted) {
      if (isValid(converted)) {
        commissionDueDate = converted.toISOString()
        
        if (dealStartDate && isBeforeDate(converted, dealStartDate)) {
          error = COMMISSION_DATE_EARLIER
        } else if (isDateInPast(converted) && !isPastDateAllowed(converted)) {
          error = COMMISSION_DUE_DATE_IN_PAST
        }
      } else {
        error = INVALID_DATE_MESSAGE
      }
    }

    setDateError(error);
    setFormData(prev => ({
      ...prev,
      commissionDueDate: commissionDueDate,
    }))

  }, [dealStartDate, isPastDateAllowed])

  return (
    <>
      <Box>
        <Button
          variant="contained"
          onClick={handleOpenDialog}
          disabled={disabled}
          className={formData.ids.length > 0 ? classes.addButtonSelected : classes.addButton}
          fullWidth
        >
          Add ISO
        </Button>
      </Box>

      {/* Main Dialog */}
      <Dialog open={isDialogOpen} onClose={handleCloseDialog} className={classes.dialog}>
        <DialogContent className={classes.dialogContent}>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" className={classes.dialogHeader}>
            <Typography variant="h4">Add ISO</Typography>
            <IconButton aria-label="close" onClick={handleCloseDialog} className={classes.closeButton}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Comission due date section */}
          {!hideCommissionDueDateField && (<div className={classes.section}>
            <DatePicker
                disabled={disabled || disableCommissionDueDateField}
                name="commissionDate"
                value={formData.commissionDueDate ? formData.commissionDueDate : null}
                shouldDisableDate={((date) => {
                  if (!date) return false;
                  if (isDateInPast(date) && !isPastDateAllowed(date)) return true;
                  if (!dealStartDate) return false;
                  return isBeforeDate(date, dealStartDate);
                })}
                onChange={handleCommissionDateChange}
                error={Boolean(dateError)}
                helperText={dateError}
                inputVariant="outlined"
                variant="dialog"
                type="keyboard"
                fullWidth
                label="Commission Due Date"
              />
              {disableCommissionDueDateField && (
                <FormHelperText error>{COMMISSION_WAS_PAID_WARNING}</FormHelperText>
              )}
          </div>
          )}

          {/* Selection Section */}
          <div className={classes.section}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ISOAutoComplete
                  multiple
                  disabled={disabled}
                  label="ISO"
                  isoId={formData.ids}
                  onChange={(_e, selectedValue: IIsoListItemLight[]) => {
                    const ids = selectedValue.map(v => v.id)
                    handleIdsChange(ids)
                  }}
                />
              </Grid>
              {selectedItems.length > 0 && (
                <Grid item xs={12}>
                  <div className={classes.selectedItems}>
                    <Typography variant="caption" color="textSecondary">
                      Selected ISOs:
                    </Typography>
                    <Box mt={1}>
                      {selectedItems.map((item: any) => (
                        <Chip key={item.id} label={item.businessName} className={classes.chip} size="small" />
                      ))}
                    </Box>
                  </div>
                </Grid>
              )}
            </Grid>
          </div>

          {/* Fee Section */}
          <div className={classes.section}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <DecimalTextField
                  variant="outlined"
                  fullWidth
                  label="ISO Fee"
                  value={formData.fee}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFeeChange(e.target.value)}
                  disabled={disabled}
                  decimalScale={DECIMAL_PLACES}
                  helperText={(() => {
                    const feeAsNumber = parseFloat(formData.fee) || 0
                    return formData.ids.length > 0 && advanceAmount * 0.15 < feeAsNumber
                      ? COMISSION_NEED_TO_BE_LOWER_THAN_15_PERCENT
                      : feeAsNumber > 0
                      ? `${calcProportionInPercents(advanceAmount, feeAsNumber)}% of advance amount`
                      : 'Enter fee amount'
                  })()}
                  error={(() => {
                    const feeAsNumber = parseFloat(formData.fee) || 0
                    return formData.ids.length > 0 && advanceAmount * 0.15 < feeAsNumber
                  })()}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          disabled={!(parseFloat(formData.fee) || 0) || !formData.ids.length || disabled}
                          aria-label="Split commission to ISOs"
                          onClick={e => setSplitPopoverAnchor(e.currentTarget)}
                        >
                          <SplitIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </div>

          <div className={classes.buttonContainer}>
            <Button onClick={handleCloseDialog} color="secondary" variant="contained" className={classes.cancelButton}>
              Cancel
            </Button>
            <Button onClick={handleSave} 
              color="primary" 
              variant="contained" 
              type="submit" 
              className={classes.saveButton}
              disabled={Boolean(dateError)}>
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Split Commission Popover */}
      <Popover
        open={Boolean(splitPopoverAnchor)}
        anchorEl={splitPopoverAnchor}
        onClose={() => setSplitPopoverAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <div className={classes.popoverContainer}>{renderSplitCommissionForm()}</div>
      </Popover>
    </>
  )
}

export default ISOSelector
